// Wires core/errorReports to the page (shared/API.md §45). Signed out (no MMUSERID cookie) = nothing is sent.
import { DlpBlock } from '../core/dlpBlock';
import { buildReport, ERROR_REPORT_PATH, ErrorReportGate } from '../core/errorReports';

function cookie(name: string): string | null {
  const m = new RegExp(`(?:^|; )${name}=([^;]*)`).exec(document.cookie);
  return m ? decodeURIComponent(m[1]) : null;
}

export function installErrorReporting(): void {
  if (!import.meta.env.PROD) return; // a dev server's errors are the developer's console, not Core's
  const gate = new ErrorReportGate();
  const send = (message: string, stack: string | null | undefined, where: 'error' | 'unhandledrejection') => {
    // A DLP filter's refusal is the person's computer, not a fault of ours: the banner tells them, and
    // a report would only repeat it in the crash digest (maria.serralde ×2 AFTER the banner shipped).
    if (DlpBlock.matches(message)) {
      DlpBlock.notify();
      return;
    }
    const userId = cookie('MMUSERID');
    if (!userId || !gate.allow(message, stack)) return;
    const body = buildReport({
      message, stack, where, at: Date.now(), version: __APP_VERSION__, build: __APP_BUILD__, userId, userAgent: navigator.userAgent,
    });
    try {
      void fetch(ERROR_REPORT_PATH, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).catch(() => undefined);
    } catch {
      /* reporting must never be the second failure */
    }
  };
  window.addEventListener('error', (event) => {
    const error = event.error as Error | undefined;
    send(String(error?.message ?? event.message ?? ''), error?.stack ?? (event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : null), 'error');
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason as Error | string | undefined;
    // The tab handed the chat to another tab ("Usar aquí"): requests still in flight are dropped on purpose.
    if (typeof reason === 'object' && reason?.name === 'WorkerStopped') return;
    const message = typeof reason === 'string' ? reason : String(reason?.message ?? reason ?? '');
    // WebKit rejects what a page still awaited as it unloads or enters the back/forward cache: the page
    // leaving, not a fault (diego.coss, iPhone, 2026-11-04).
    if (/browsing context is going away/i.test(message)) return;
    send(message, typeof reason === 'object' ? reason?.stack : null, 'unhandledrejection');
  });
}
